import { Box, Typography } from "@material-ui/core";
import {
  HotelName,
  HotelCarousel,
  HotelOverview,
  HotelAmenities,
  HotelTripAdvisorReviews,
  HotelDescription,
  BackToTopButton,
  HotelPriceCompareChart,
  HotelShopRoomTypePickerVariant,
  savingsAmountBasedOnCheapestProduct,
  TrackingEventControlType,
  getCancellationPolicyInfo,
  VoidWindowNotice,
  CheckInInstructions,
  getCancellationPolicyInfoV2,
  SanitizedHtml,
} from "halifax";
import React, { useState, useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import clsx from "clsx";
import {
  CancellationPolicyEnum,
  CancellationReason,
  HotelCancellationPolicyV2Enum,
  SelectedTravelOfferScreen,
} from "redmond";
import Skeleton from "react-loading-skeleton";
import { useIsTravelApp } from "@capone/common";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";
import { RewardsAccountSelection } from "../../../rewards/components";
import { CALIFORNIA_BILL_644_CANCELLATION_TEXT } from "../DesktopShop/textConstants";
import { HotelShopExpandedMap } from "../HotelShopExpandedMap";
import { HotelShopSmallMap } from "../HotelShopSmallMap/component";
import { MobileShopConnectorProps } from "./container";
import { MobileHotelPhotoModal } from "../MobileHotelPhotoModal";
import * as textConstants from "./textConstants";
import { config } from "../../../../api/config";
import {
  PackageHotelShopCallState,
  PackageHotelShopStep,
} from "../../reducer/state";
import { MobileRoomPicker } from "../MobileRoomPicker";
import { MobileHotelShopRoomTypePickerPanel } from "../MobileHotelShopRoomTypePickerPanel";
import { MobileShopHeader } from "../MobileShopHeader";
import { PackagesShopProgressBar } from "../../../book/components";
import { goToAvailability } from "../../utils/queryStringHelpers";
import { ShopErrorModal } from "../ShopErrorModal";
import "./styles.scss";
import { TravelWalletDetailsBanner } from "../../../travel-wallet/components";
import {
  HOTELS_CANCELLATION_POLICY_EXPERIMENT,
  PACKAGES_LIST_OPTIMIZATIONS_V1,
  useExperiments,
} from "../../../../context/experiments";
import dayjs from "dayjs";

export interface IMobileShopProps
  extends MobileShopConnectorProps,
    RouteComponentProps {
  roomInfoProductsType?: HotelShopRoomTypePickerVariant;
  handleReadyToRedirect?: (
    trackingEventControl?: TrackingEventControlType
  ) => void;
}

export const MobileShop = (props: IMobileShopProps) => {
  const {
    lodging,
    hotelShopCallInProgress,
    cheapestProduct,
    chosenProduct,
    hasFailed,
    hotelShopStep,
    stayDates,
    fromDate,
    untilDate,
    history,
    largestValueAccount,
    adultsCount,
    children,
    hotelShopCallState,
    nightCount,
    roomInfoProductsType,
    handleReadyToRedirect,
    packagesByRateId,
    travelersCount,
    rewardsKey,
    cancellationSummary,
    origin,
    infants,
    stopsOption,
    fareClassOptionFilter,
    searchDistanceFromShopProperties,
    packagesByLodgingId,
    showOfferBasedOnSelectedLodging,
    setPackageHotelShopProgress,
  } = props;
  const [showExpandedHotelMap, setShowExpandedHotelMap] = useState(false);
  const [openPhotoModal, setOpenPhotoModal] = useState(false);
  const [successShopSearchFromDate, setSuccessShopSearchFromDate] =
    useState<Date | null>(null);
  const [successShopSearchUntilDate, setSuccessShopSearchUntilDate] =
    useState<Date | null>(null);

  const { isTravelApp } = useIsTravelApp();

  const expState = useExperiments();

  const cancellationPolicyExperimentEnabled =
    getExperimentVariant(
      expState.experiments,
      HOTELS_CANCELLATION_POLICY_EXPERIMENT
    ) === AVAILABLE;
  const cancellationPolicy = cheapestProduct?.cancellationPolicy
    ? getCancellationPolicyInfo(
        cheapestProduct?.cancellationPolicy,
        textConstants.getCfarSecondaryText
      )
    : null;
  const cancellationPolicyV2 = cheapestProduct?.cancellationPolicyV2
    ? getCancellationPolicyInfoV2(cheapestProduct?.cancellationPolicyV2)
    : null;
  const cancellationPolicyInfo = cancellationPolicyExperimentEnabled
    ? cancellationPolicyV2
    : cancellationPolicy;
  const satisfiesCaliforniaBill644 = Boolean(
    cancellationPolicyExperimentEnabled
      ? cheapestProduct?.cancellationPolicyV2?.CancellationPolicyV2 ===
          HotelCancellationPolicyV2Enum.NonRefundable &&
          cancellationSummary?.reasons.includes(
            CancellationReason.CaliforniaBill644
          )
      : cheapestProduct?.cancellationPolicy.CancellationPolicy ===
          CancellationPolicyEnum.NonRefundable &&
          cancellationSummary?.reasons.includes(
            CancellationReason.CaliforniaBill644
          )
  );

  const packagesListEnhancementsEnabled =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_LIST_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  useEffect(() => {
    if (hotelShopCallState === PackageHotelShopCallState.Success) {
      setSuccessShopSearchFromDate(fromDate);
      setSuccessShopSearchUntilDate(untilDate);
    }
  }, [hotelShopCallState]);

  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 100);
  }, [hotelShopStep]);

  useEffect(() => {
    // travel app hides back button in choose room picker, so always reset step to hotel info on mount
    if (isTravelApp) {
      setPackageHotelShopProgress(PackageHotelShopStep.HotelInfo);
    }
  }, []);

  const returnToAvailability = (
    prevSuccessHotelFromDate?: Date | null,
    prevSuccessHotelUntilDate?: Date | null
  ) => {
    goToAvailability({
      history,
      origin,
      lodging,
      fromDate,
      untilDate,
      adultsCount,
      children,
      infants,
      stopsOption,
      prevSuccessHotelFromDate,
      prevSuccessHotelUntilDate,
      fareClassOptionFilter,
    });
  };

  const renderSkeleton = () => (
    <Box className="mobile-hotel-shop-skeleton">
      {!lodging && (
        <>
          <Box>
            <HotelName isSkeleton />
          </Box>
          <HotelCarousel isSkeleton />
          <Box className="section-wrapper">
            <HotelOverview isSkeleton />
            <HotelAmenities isSkeleton />
            <HotelOverview isSkeleton />
          </Box>
          <Box className="section-wrapper">
            <HotelAmenities isSkeleton />
            <HotelAmenities isSkeleton />
            <HotelOverview isSkeleton />
          </Box>
          <Box className="section-wrapper">
            <HotelName isSkeleton />
            <HotelCarousel isSkeleton />
          </Box>
        </>
      )}

      <Box className={clsx("mobile-hotel-shop-price-panel", "skeleton")}>
        <Skeleton className="hotel-shop-choose-room-cta-skeleton" />
      </Box>
    </Box>
  );
  const renderRewardsAccount = () => (
    <Box className="mobile-rewards-account-contents">
      <RewardsAccountSelection className="b2b" popoverClassName="b2b" />
    </Box>
  );

  const renderAvailability = (hotelShopStep: PackageHotelShopStep) => {
    const { lodging: lodgingData, price } = lodging || {};
    const savings =
      price && cheapestProduct
        ? savingsAmountBasedOnCheapestProduct(price, cheapestProduct)
        : null;

    return lodging && hotelShopStep === PackageHotelShopStep.HotelInfo ? (
      <Box className="mobile-hotel-shop-container">
        <MobileShopHeader />
        <PackagesShopProgressBar isMobile />
        {renderRewardsAccount()}
        <MobileHotelPhotoModal
          openModal={openPhotoModal}
          setOpenModal={setOpenPhotoModal}
        />
        <div
          onClick={() => {
            setOpenPhotoModal(true);
          }}
        >
          <HotelCarousel media={lodgingData?.media} />
        </div>
        {showOfferBasedOnSelectedLodging && (
          <TravelWalletDetailsBanner
            offer={lodging.bestOfferThisLodging}
            className="mobile-pkg-hotel-shop-offer"
            screen={SelectedTravelOfferScreen.PACKAGES_HOTEL_SHOP}
            bannerTextType="shop"
          />
        )}
        {cancellationSummary?.reasons.includes(
          CancellationReason.CaliforniaBill644
        ) && <VoidWindowNotice />}
        <Box className={clsx("section-wrapper", "overview")}>
          <HotelOverview
            lodging={lodging}
            includePricing
            chosenProduct={chosenProduct ?? cheapestProduct}
            nightCount={nightCount ?? 0}
            rewardsKey={rewardsKey ?? undefined}
            packagesByRateId={packagesByRateId}
            isPackageHotel
            travelersCount={travelersCount}
            showEnhancedPackagePricing={packagesListEnhancementsEnabled}
          />
        </Box>
        <Box className={clsx("section-wrapper", "amenities")}>
          <Typography variant="h4">Amenities</Typography>
          <HotelAmenities
            amenities={lodgingData?.amenities}
            displayCount={4}
            showAmenitiesText={`${textConstants.SHOW_ALL_AMENITIES_TEXT} (${
              lodgingData?.amenities && lodgingData?.amenities?.length - 4
            })`}
            hideAmenityIconsInTooltip
          />
        </Box>
        {price &&
          price.comparePrices.length > 0 &&
          !!price.comparePrices.find((price) => price.source !== "Expedia") &&
          cheapestProduct &&
          savings && (
            <Box className={clsx("section-wrapper", "compare")}>
              <Typography variant="h4">Compare Prices</Typography>
              <HotelPriceCompareChart
                lodgingPrice={price}
                siteName="Capital One"
                cheapestProduct={cheapestProduct}
                earnText={textConstants.getEarnText(
                  largestValueAccount.earn.hotelsMultiplier,
                  largestValueAccount.productDisplayName,
                  largestValueAccount.rewardsBalance.currencyDescription ??
                    largestValueAccount.rewardsBalance.currency
                )}
              />
            </Box>
          )}
        <Box className={clsx("section-wrapper", "property")}>
          <Typography variant="h4">About the Property</Typography>
          <HotelDescription
            description={lodging?.lodging?.description}
            onMapClick={() => {
              setShowExpandedHotelMap(true);
            }}
            map={
              <HotelShopSmallMap
                lodging={lodging}
                setShowExpandedHotelMap={setShowExpandedHotelMap}
                searchDistanceFromShopProperties={
                  searchDistanceFromShopProperties
                }
                lodgingPackage={packagesByLodgingId?.[lodging.lodging.id]}
              />
            }
          />
          <HotelShopExpandedMap
            isMobile
            lodging={lodging}
            showExpandedHotelMap={showExpandedHotelMap}
            setShowExpandedHotelMap={setShowExpandedHotelMap}
          />
        </Box>
        {lodgingData?.tripAdvisorReviews &&
          lodgingData?.tripAdvisorReviews?.comments?.length > 0 && (
            <Box className={clsx("section-wrapper", "customer")}>
              <Typography variant="h4">Customer Reviews</Typography>
              <HotelTripAdvisorReviews
                lodgingData={lodgingData}
                trim={500}
                isMobile
              />
            </Box>
          )}
        {renderCancellationPolicyInfo(
          cancellationPolicyInfo,
          satisfiesCaliforniaBill644
        )}
        {lodgingData?.checkInInstructions &&
          stayDates?.from &&
          stayDates?.until && (
            <Box className={clsx("section-wrapper", "check-in-schedule")}>
              <Typography variant="h4">
                {textConstants.CHECK_IN_INSTRUCTIONS_TITLE}
              </Typography>
              <SanitizedHtml
                as={Typography}
                variant="subtitle2"
                html={textConstants.formattedCheckIn(
                  dayjs(stayDates.from).toDate(),
                  lodgingData.checkInInstructions?.checkInTime
                )}
              />
              <SanitizedHtml
                as={Typography}
                variant="body2"
                html={textConstants.formattedCheckOut(
                  dayjs(stayDates.until).toDate(),
                  lodgingData.checkInInstructions?.checkOutTime
                )}
              />
            </Box>
          )}{" "}
        {lodgingData?.checkInInstructions &&
        lodgingData?.checkInInstructions?.policies?.length ? (
          <Box className={clsx("section-wrapper", "check-in-instruction")}>
            <CheckInInstructions
              policies={lodgingData?.checkInInstructions?.policies}
            />
          </Box>
        ) : undefined}
        <BackToTopButton bottom={110} />
        <MobileHotelShopRoomTypePickerPanel />
      </Box>
    ) : (
      <></>
    );
  };

  const renderShop = (hotelShopStep: PackageHotelShopStep) => (
    <Box className="mobile-hotel-shop-container">
      {lodging && hotelShopStep === PackageHotelShopStep.HotelInfo ? (
        <BackToTopButton bottom={110} />
      ) : (
        <MobileRoomPicker
          handleReadyToRedirect={handleReadyToRedirect}
          roomInfoProductsType={roomInfoProductsType}
          nonRefundablePolicyOverrideText={
            cancellationSummary?.reasons.includes(
              CancellationReason.CaliforniaBill644
            )
              ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
              : undefined
          }
        />
      )}
    </Box>
  );

  const renderCancellationPolicyInfo = (
    cancellationPolicyInfo: {
      className: string;
      primaryText: string;
      secondaryText?: string;
      tagText: String;
    } | null,
    satisfiesCaliforniaBill644: boolean
  ) => {
    if (cancellationPolicyExperimentEnabled) {
      return (
        satisfiesCaliforniaBill644 &&
        cancellationPolicyInfo && (
          <Box className={clsx("section-wrapper", "cancellation")}>
            <Typography variant="h4">
              {cancellationPolicyInfo.primaryText}
            </Typography>
            <Typography variant="subtitle2">
              {CALIFORNIA_BILL_644_CANCELLATION_TEXT}
            </Typography>
          </Box>
        )
      );
    } else {
      return (
        cancellationPolicyInfo && (
          <Box className={clsx("section-wrapper", "cancellation")}>
            <Typography variant="h4">
              {cancellationPolicyInfo.primaryText}
            </Typography>
            <Typography variant="subtitle2">
              {satisfiesCaliforniaBill644
                ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
                : cancellationPolicyInfo.secondaryText}
            </Typography>
          </Box>
        )
      );
    }
  };

  return !hasFailed ? (
    <Box
      className={clsx(
        "mobile-package-hotel-shop-wrapper",
        config.TENANT,
        "global-mobile-nav",
        { "travel-app": isTravelApp }
      )}
    >
      {renderAvailability(hotelShopStep)}
      {hotelShopCallInProgress &&
      hotelShopStep === PackageHotelShopStep.HotelInfo
        ? renderSkeleton()
        : renderShop(hotelShopStep)}
    </Box>
  ) : (
    <ShopErrorModal
      returnToAvailability={() =>
        returnToAvailability(
          successShopSearchFromDate,
          successShopSearchUntilDate
        )
      }
    />
  );
};
